import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SignatureValue, toSignatureRequest } from 'src/app/core/helpers/signature.helpers';
import { BaseService } from 'src/app/core/services/base.service';
import { BaseResponse } from 'src/app/core/models/responses/base-response';
import { BasePagingRequest } from 'src/app/core/models/requests/base-paging-request';
import { BasePagingResponse } from 'src/app/core/models/responses/base-paging-response';
import {
  CashTransactionDeclaration,
  CashTransactionDeclarationFilter,
  CashTransactionDeclarationRequest,
  CashTransactionDeclarationUpdateRequest,
  CustomerIdentityVerification,
  CustomerIdentityVerificationFilter,
  CustomerIdentityVerificationRequest,
  CustomerIdentityVerificationUpdateRequest,
  MasakThreshold,
  MasakThresholdCheckRequest,
  MasakThresholdCheckResult,
} from 'src/app/models/masak/masak-compliance';
import {
  MasakSelfServiceApproveRequest,
  MasakSelfServiceApproveResult,
  MasakSelfServiceCode,
  MasakSelfServiceDetail,
  MasakSelfServiceFilter,
  MasakSelfServiceListItem,
} from 'src/app/models/masak/masak-self-service';
import {
  ManualSanctionScreeningRequest,
  SanctionListStatus,
  SanctionScreening,
  SanctionScreeningFilter,
  SanctionScreeningReviewRequest,
} from 'src/app/models/masak/masak-sanction-screening';

/**
 * MASAK kimlik tespiti (KYC) eşiği ve kayıtları — bkz. backend masak-uyum.md.
 * Kayıt uçları (create/list/find) `/identity-verifications/...` altında, BaseService'in
 * standart CRUD desenine (POST /create, POST /list, GET /{id}) uyacak şekilde kuruldu.
 */
@Injectable({ providedIn: 'root' })
export class MasakComplianceService extends BaseService<
  CustomerIdentityVerification,
  CustomerIdentityVerificationFilter,
  number
> {
  private settingsUrl = environment.urlAddress + '/MasakCompliance';

  constructor(private http: HttpClient) {
    super(http, environment.urlAddress + '/MasakCompliance/identity-verifications');
  }

  createVerification(
    request: CustomerIdentityVerificationRequest,
  ): Observable<BaseResponse<CustomerIdentityVerification>> {
    return this.create(request as CustomerIdentityVerification);
  }

  /** Müşteri bağlantısı ve belge görseli değişmez — bkz. CustomerIdentityVerificationUpdateRequest. */
  updateVerification(
    id: number,
    request: CustomerIdentityVerificationUpdateRequest,
  ): Observable<BaseResponse<CustomerIdentityVerification>> {
    return this._http.put<BaseResponse<CustomerIdentityVerification>>(`${this._base}/${id}`, request);
  }

  /**
   * Kimlik belgesi (fotoğraf ya da PDF) yükler. `side`: 'front' (varsayılan) ya da
   * 'back' — 'back' yalnızca T.C. Kimlik Kartı'nda (CustomerIdentityDocumentType.Tckn)
   * geçerlidir, backend diğer belge türlerinde reddeder.
   */
  uploadDocument(
    id: number,
    file: File,
    side?: 'front' | 'back',
  ): Observable<BaseResponse<CustomerIdentityVerification>> {
    const formData = new FormData();
    formData.append('file', file);
    const params = side ? { side } : undefined;
    return this._http.post<BaseResponse<CustomerIdentityVerification>>(
      `${this._base}/${id}/document`,
      formData,
      { params },
    );
  }

  /** Ek belge (PDF/JPG/PNG, en fazla 10 MB) yükler — tüzel kişide ticaret sicil gazetesi, imza sirküleri vb. */
  uploadAttachment(id: number, file: File): Observable<BaseResponse<CustomerIdentityVerification>> {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post<BaseResponse<CustomerIdentityVerification>>(`${this._base}/${id}/attachments`, formData);
  }

  getAttachmentBlob(id: number, attachmentId: number): Observable<Blob> {
    return this._http.get(`${this._base}/${id}/attachments/${attachmentId}`, { responseType: 'blob' });
  }

  deleteAttachment(id: number, attachmentId: number): Observable<BaseResponse<CustomerIdentityVerification>> {
    return this._http.delete<BaseResponse<CustomerIdentityVerification>>(`${this._base}/${id}/attachments/${attachmentId}`);
  }

  /** Kimlik belgesi görseli/PDF'i — yalnızca yüklenmişse. authToken/branchId JwtInterceptor ile otomatik eklenir. */
  getDocumentBlob(id: number, side?: 'front' | 'back'): Observable<Blob> {
    const params = side ? { side } : undefined;
    return this._http.get(`${this._base}/${id}/document`, { responseType: 'blob', params });
  }

  // ── Self-servis başvuruları (müşterinin QR ile açtığı form) — personel tarafı ─────

  /** QR'daki süreli kod; süresi dolduysa sunucu yenisini üretir. */
  getSelfServiceCode(): Observable<BaseResponse<MasakSelfServiceCode>> {
    return this._http.get<BaseResponse<MasakSelfServiceCode>>(this.settingsUrl + '/self-service/code');
  }

  getAllSelfService(
    request: BasePagingRequest<MasakSelfServiceFilter>,
  ): Observable<BasePagingResponse<MasakSelfServiceListItem>> {
    return this._http.post<BasePagingResponse<MasakSelfServiceListItem>>(this.settingsUrl + '/self-service/list', request);
  }

  getSelfService(id: number): Observable<BaseResponse<MasakSelfServiceDetail>> {
    return this._http.get<BaseResponse<MasakSelfServiceDetail>>(`${this.settingsUrl}/self-service/${id}`);
  }

  getSelfServiceFileBlob(id: number, fileId: number): Observable<Blob> {
    return this._http.get(`${this.settingsUrl}/self-service/${id}/files/${fileId}`, { responseType: 'blob' });
  }

  /** Müşteri kartı (customerId yoksa yeni), kimlik tespiti ve varsa nakit beyan birlikte oluşur. */
  approveSelfService(id: number, request: MasakSelfServiceApproveRequest): Observable<BaseResponse<MasakSelfServiceApproveResult>> {
    return this._http.post<BaseResponse<MasakSelfServiceApproveResult>>(`${this.settingsUrl}/self-service/${id}/approve`, request);
  }

  rejectSelfService(id: number, note?: string): Observable<BaseResponse<any>> {
    return this._http.post<BaseResponse<any>>(`${this.settingsUrl}/self-service/${id}/reject`, { note });
  }

  // ── Islak imzalı form (kimlik tespiti ve nakit beyan ortak) ──────────────────

  // ── Ekranda atılan imza ───────────────────────────────────────────────────

  /** Kalem izlerini gönderir; SVG'yi sunucu üretir. Varsa eski imzanın yerine geçer. */
  saveSignature(kind: 'identity' | 'cash', id: number, value: SignatureValue): Observable<BaseResponse<any>> {
    return this._http.put<BaseResponse<any>>(`${this.signedFormBase(kind, id)}/signature`, toSignatureRequest(value));
  }

  deleteSignature(kind: 'identity' | 'cash', id: number): Observable<BaseResponse<any>> {
    return this._http.delete<BaseResponse<any>>(`${this.signedFormBase(kind, id)}/signature`);
  }

  private signedFormBase(kind: 'identity' | 'cash', id: number): string {
    return kind === 'identity'
      ? `${this._base}/${id}`
      : `${this.settingsUrl}/cash-declarations/${id}`;
  }

  /**
   * Müşteriye imzalatılacak, kayıttan üretilen form (PDF). signed=false ise ekranda atılmış imza
   * basılmaz — ıslak imza için boş form.
   */
  getFormPdf(kind: 'identity' | 'cash', id: number, signed = true): Observable<Blob> {
    const query = signed ? '' : '?signed=false';
    return this._http.get(`${this.signedFormBase(kind, id)}/form-pdf${query}`, { responseType: 'blob' });
  }

  /** İmzalı formu yükler (PDF/JPG/PNG, en fazla 10 MB) — varsa eskisinin yerine geçer. */
  uploadSignedForm(kind: 'identity' | 'cash', id: number, file: File): Observable<BaseResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post<BaseResponse<any>>(`${this.signedFormBase(kind, id)}/signed-form`, formData);
  }

  getSignedFormBlob(kind: 'identity' | 'cash', id: number): Observable<Blob> {
    return this._http.get(`${this.signedFormBase(kind, id)}/signed-form`, { responseType: 'blob' });
  }

  deleteSignedForm(kind: 'identity' | 'cash', id: number): Observable<BaseResponse<any>> {
    return this._http.delete<BaseResponse<any>>(`${this.signedFormBase(kind, id)}/signed-form`);
  }

  // ── KPI sayıları — liste uçlarının toplam kayıt sayısı (tek satırlık sayfa istenir) ──

  countIdentityVerifications(filter: CustomerIdentityVerificationFilter): Observable<number> {
    return this.countOf(this._base + '/list', filter);
  }

  countCashDeclarations(filter: CashTransactionDeclarationFilter): Observable<number> {
    return this.countOf(this.settingsUrl + '/cash-declarations/list', filter);
  }

  countSelfService(filter: MasakSelfServiceFilter): Observable<number> {
    return this.countOf(this.settingsUrl + '/self-service/list', filter);
  }

  countScreenings(filter: SanctionScreeningFilter): Observable<number> {
    return this.countOf(this.settingsUrl + '/screenings/list', filter);
  }

  private countOf(url: string, filter: object): Observable<number> {
    return this._http
      .post<BasePagingResponse<unknown>>(url, { pageIndex: 1, itemPerPage: 1, filter })
      .pipe(map((res) => res?.data?.TotalItemCount ?? 0));
  }

  getThreshold(): Observable<BaseResponse<MasakThreshold>> {
    return this._http.get<BaseResponse<MasakThreshold>>(this.settingsUrl + '/threshold');
  }

  updateThreshold(request: MasakThreshold): Observable<BaseResponse<MasakThreshold>> {
    return this._http.put<BaseResponse<MasakThreshold>>(this.settingsUrl + '/threshold', request);
  }

  checkThreshold(
    request: MasakThresholdCheckRequest,
  ): Observable<BaseResponse<MasakThresholdCheckResult>> {
    return this._http.post<BaseResponse<MasakThresholdCheckResult>>(this.settingsUrl + '/check', request);
  }

  // ── Yaptırım listesi taraması (OFAC SDN, BM, AB, TR malvarlığı dondurma) ────

  /** Kaynak başına kayıt sayısı ve son aktarım. */
  getSanctionListStatus(): Observable<BaseResponse<SanctionListStatus>> {
    return this._http.get<BaseResponse<SanctionListStatus>>(this.settingsUrl + '/sanction-lists/status');
  }

  /** Kimlik tespiti kaydındaki bilgilerle (ad, doğum yılı; tüzel kişide unvan + yetkili) yeni tarama. */
  screenVerification(id: number): Observable<BaseResponse<SanctionScreening>> {
    return this._http.post<BaseResponse<SanctionScreening>>(`${this._base}/${id}/screen`, {});
  }

  /** Müşteri kartındaki adla tarama — kimlik tespiti kaydı olmayan müşteride de çalışır. */
  screenCustomer(customerId: number): Observable<BaseResponse<SanctionScreening>> {
    return this._http.post<BaseResponse<SanctionScreening>>(`${this.settingsUrl}/customers/${customerId}/screen`, {});
  }

  /** Müşteri kartı olmadan, elle girilen TCKN ve/veya ad soyadla tarama — sonuç müşteriye bağlanmaz. */
  screenManual(request: ManualSanctionScreeningRequest): Observable<BaseResponse<SanctionScreening>> {
    return this._http.post<BaseResponse<SanctionScreening>>(`${this.settingsUrl}/screenings/manual`, request);
  }

  getAllScreenings(
    request: BasePagingRequest<SanctionScreeningFilter>,
  ): Observable<BasePagingResponse<SanctionScreening>> {
    return this._http.post<BasePagingResponse<SanctionScreening>>(this.settingsUrl + '/screenings/list', request);
  }

  getScreening(id: number): Observable<BaseResponse<SanctionScreening>> {
    return this._http.get<BaseResponse<SanctionScreening>>(`${this.settingsUrl}/screenings/${id}`);
  }

  /** Olası eşleşmeyi karara bağlar (Yanlış pozitif / Gerçek eşleşme) — gerekçe zorunlu. */
  reviewScreening(id: number, request: SanctionScreeningReviewRequest): Observable<BaseResponse<SanctionScreening>> {
    return this._http.put<BaseResponse<SanctionScreening>>(`${this.settingsUrl}/screenings/${id}/review`, request);
  }

  // ── Nakit İşlem Beyan Formu (2.000.001-20.000.000 TL) ──────────────────────

  createCashDeclaration(
    request: CashTransactionDeclarationRequest,
  ): Observable<BaseResponse<CashTransactionDeclaration>> {
    return this._http.post<BaseResponse<CashTransactionDeclaration>>(
      this.settingsUrl + '/cash-declarations/create',
      request,
    );
  }

  getAllCashDeclarations(
    request: BasePagingRequest<CashTransactionDeclarationFilter>,
  ): Observable<BasePagingResponse<CashTransactionDeclaration>> {
    const itemPerPage = localStorage.getItem('itemPerPage');
    request.itemPerPage = itemPerPage ? parseInt(itemPerPage, 10) : 10;
    return this._http.post<BasePagingResponse<CashTransactionDeclaration>>(
      this.settingsUrl + '/cash-declarations/list',
      request,
    );
  }

  getCashDeclaration(id: number): Observable<BaseResponse<CashTransactionDeclaration>> {
    return this._http.get<BaseResponse<CashTransactionDeclaration>>(
      `${this.settingsUrl}/cash-declarations/${id}`,
    );
  }

  /**
   * Nakit beyan eki yükler (PDF/JPG/PNG, en fazla 10 MB). kind 'fund-source' = fon kaynağı
   * beyanı belgesi (kayıt başına tek — yenisi eskisinin yerini alır), 'other' = ek belge.
   */
  uploadCashAttachment(
    id: number,
    file: File,
    kind: 'fund-source' | 'other',
  ): Observable<BaseResponse<CashTransactionDeclaration>> {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post<BaseResponse<CashTransactionDeclaration>>(
      `${this.settingsUrl}/cash-declarations/${id}/attachments`,
      formData,
      { params: { kind } },
    );
  }

  getCashAttachmentBlob(id: number, attachmentId: number): Observable<Blob> {
    return this._http.get(`${this.settingsUrl}/cash-declarations/${id}/attachments/${attachmentId}`, { responseType: 'blob' });
  }

  deleteCashAttachment(id: number, attachmentId: number): Observable<BaseResponse<CashTransactionDeclaration>> {
    return this._http.delete<BaseResponse<CashTransactionDeclaration>>(
      `${this.settingsUrl}/cash-declarations/${id}/attachments/${attachmentId}`,
    );
  }

  /** Müşteri bağlantısı değişmez — bkz. CashTransactionDeclarationUpdateRequest. */
  updateCashDeclaration(
    id: number,
    request: CashTransactionDeclarationUpdateRequest,
  ): Observable<BaseResponse<CashTransactionDeclaration>> {
    return this._http.put<BaseResponse<CashTransactionDeclaration>>(
      `${this.settingsUrl}/cash-declarations/${id}`,
      request,
    );
  }
}
